import { h } from 'vue'
import { useStateStore } from '@json-render/vue'
import { z } from 'zod'

import EventsPageHeader from '../components/molecules/EventsPageHeader.vue'
import EventsListSection from '../components/organisms/EventsListSection.vue'
import LiveNowCarousel from '../components/organisms/LiveNowCarousel.vue'

const resourceStateSchema = z.enum(['loading', 'empty', 'error', 'ready'])
const eventCardSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    imageUrl: z.string(),
    dateLabel: z.string(),
    locationLabel: z.string(),
    attendanceLabel: z.string().optional(),
    description: z.string().optional(),
    status: z.enum(['live', 'registered', 'upcoming', 'recording', 'past']).optional(),
    isLive: z.boolean().optional(),
    isRegistered: z.boolean().optional(),
    imageFocalPoint: z.object({ x: z.number(), y: z.number() }).strict().optional(),
    timeLabel: z.string().optional(),
    dateChip: z.string().optional(),
    startAt: z.string().optional(),
    endAt: z.string().optional(),
    timeZone: z.string().optional(),
  })
  .strict()

const headerProps = z
  .object({
    heading: z.enum(['events', 'live', 'upcoming', 'past']).optional(),
    title: z.string().optional(),
    description: z.string().optional(),
  })
  .strict()

const liveNowProps = z
  .object({
    state: resourceStateSchema,
    events: z.array(eventCardSchema),
    itemLimit: z.number().optional(),
    density: z.enum(['compact', 'comfortable', 'roomy']).optional(),
    total: z.number().optional(),
  })
  .strict()

const listProps = z
  .object({
    upcomingState: resourceStateSchema,
    upcomingEvents: z.array(eventCardSchema),
    pastState: resourceStateSchema,
    pastEvents: z.array(eventCardSchema),
    itemLimit: z.number().optional(),
    density: z.enum(['compact', 'comfortable', 'roomy']).optional(),
    upcomingTotal: z.number().optional(),
    pastTotal: z.number().optional(),
  })
  .strict()

export const EVENTS_HUB_EXPERIENCE_COMPONENT_DEFINITIONS = {
  EventsPageHeader: {
    props: headerProps,
    description: 'Title and description for events listing screens.',
  },
  EventsLiveNow: {
    props: liveNowProps,
    description: 'Live events carousel for the Events Hub.',
  },
  EventsList: {
    props: listProps,
    description: 'Upcoming and past event cards for the Events Hub.',
  },
} as const

interface RegistryContext<Props> {
  readonly props: Props
  readonly emit: (event: string) => void
}

const stageEvent = (set: (path: string, value: unknown) => void, eventId: string, emit: () => void) => {
  set('/ui/selectedEventId', eventId)
  emit()
}

export const EVENTS_HUB_EXPERIENCE_COMPONENTS = {
  EventsPageHeader: ({
    props,
  }: RegistryContext<z.infer<(typeof EVENTS_HUB_EXPERIENCE_COMPONENT_DEFINITIONS)['EventsPageHeader']['props']>>) =>
    h(EventsPageHeader, {
      heading: props.heading,
      title: props.title,
      description: props.description,
    }),
  EventsLiveNow: ({
    props,
    emit,
  }: RegistryContext<z.infer<(typeof EVENTS_HUB_EXPERIENCE_COMPONENT_DEFINITIONS)['EventsLiveNow']['props']>>) => {
    const { set } = useStateStore()
    return h(LiveNowCarousel, {
      state: props.state,
      events: props.events,
      itemLimit: props.itemLimit,
      total: props.total,
      onOpenEvent: (eventId: string) => stageEvent(set, eventId, () => emit('openEvent')),
      onSeeAll: () => emit('seeAll'),
    })
  },
  EventsList: ({ props, emit }: RegistryContext<z.infer<(typeof EVENTS_HUB_EXPERIENCE_COMPONENT_DEFINITIONS)['EventsList']['props']>>) => {
    const { set } = useStateStore()
    return h(EventsListSection, {
      upcomingState: props.upcomingState,
      upcomingEvents: props.upcomingEvents,
      pastState: props.pastState,
      pastEvents: props.pastEvents,
      itemLimit: props.itemLimit,
      density: props.density,
      upcomingTotal: props.upcomingTotal,
      pastTotal: props.pastTotal,
      onOpenEvent: (eventId: string) => stageEvent(set, eventId, () => emit('openEvent')),
      onSeeAll: (tab: string) => {
        set('/ui/eventsListTab', tab)
        emit('seeAll')
      },
    })
  },
} as const
